import React, { useEffect, useLayoutEffect } from "react";
import styled, { keyframes } from "styled-components";
import linhasVerticais from "../Components/Home/images/linhas-verticais.png";
import { useConfig } from "../context/ConfigContext";

// "#RRGGBB" -> "rgba(r, g, b, a)"
function hexToRgba(hex, alpha) {
  const h = (hex || "").replace("#", "");
  const r = parseInt(h.substring(0, 2), 16) || 0;
  const g = parseInt(h.substring(2, 4), 16) || 0;
  const b = parseInt(h.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Load an admin-chosen Google Font by injecting (once, per linkId) a stylesheet
// <link> for it, reusing that same link on later changes. The family name is a
// plain Google Font name (validated server-side to letters/numbers/spaces).
function ensureFontLoaded(family, linkId) {
  const name = (family || "").trim();
  if (!name) return;
  let link = document.getElementById(linkId);
  if (!link) {
    link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  const fam = encodeURIComponent(name).replace(/%20/g, "+");
  link.href =
    "https://fonts.googleapis.com/css2?family=" +
    fam +
    ":ital,wght@0,300;0,400;0,500;0,700;1,400&display=swap";
}

// Build the diagonal accent gradient from the two configured colors, keeping
// the original stop positions/alphas. Two variants (Viewport vs Pulse) differ
// only in their stop offsets.
function buildGradient(g1, g2, s1, s2, s3, s4) {
  return `linear-gradient(108.57deg, ${hexToRgba(g1, 0.55)} ${s1}, ${hexToRgba(
    g1,
    0
  )} ${s2}, ${hexToRgba(g2, 0)} ${s3}, ${hexToRgba(g2, 0.23)} ${s4})`;
}

// The whole UI is designed on a fixed 1920×1080 canvas (absolute px positions).
// Instead of making every screen responsive, we scale that canvas to fit the
// window and letterbox the rest — scale = min(vw/1920, vh/1080), centered.
const DESIGN_W = 1920;
const DESIGN_H = 1080;

// Breathing glow, moved here from the per-screen *ContainerBackground layers so
// the background is a SINGLE full-window plane — no seam at the canvas edge.
const pulse = keyframes`
  0% { opacity: 0; }
  50% { opacity: 1; }
  100% { opacity: 0; }
`;

// Base background plane — spans the whole window so the area around the scaled
// canvas reads as one continuous field (texture + gradient + base color). The
// base color and gradient come from the admin Config (props); defaults match
// the original design.
//
// When `image` is set (a per-empreendimento background uploaded in the admin),
// it replaces the gradient+texture entirely and covers the whole window; with
// no image we fall back to the configured gradient look.
const Viewport = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background-color: ${(p) => p.baseColor};
  background-image: ${(p) =>
    p.image ? `url("${p.image}")` : `${p.gradient}, url(${linhasVerticais})`};
  background-size: cover;
  /* Center + no-repeat only for the image override; the gradient path keeps the
     original defaults (top-left / repeat) so Home/Start look unchanged. */
  background-position: ${(p) => (p.image ? "center center" : "0% 0%")};
  background-repeat: ${(p) => (p.image ? "no-repeat" : "repeat")};
`;

// Animated overlay — the shifted gradient that pulses in and out over the base.
const Pulse = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: ${(p) => p.gradient}, url(${linhasVerticais});
  background-size: cover;
  animation: ${pulse} 10s infinite;
`;

const Canvas = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${DESIGN_W}px;
  height: ${DESIGN_H}px;
  transform: translate(-50%, -50%) scale(var(--stage-scale, 1));
  transform-origin: center center;
`;

export default function Stage({ children }) {
  const { appConfig, configApp } = useConfig();
  const bg = appConfig.background;
  const viewportGradient = buildGradient(bg.gradient1, bg.gradient2, "-0.28%", "32.03%", "89.43%", "110.8%");
  const pulseGradient = buildGradient(bg.gradient1, bg.gradient2, "-10.28%", "9%", "67.48%", "99.8%");

  // A screen (currently the empreendimento detail) can override the background
  // with a full-window image via configApp.stageBg; null keeps the gradient.
  const stageBg = configApp.stageBg || null;

  useEffect(() => {
    const fit = () => {
      const s = Math.min(
        window.innerWidth / DESIGN_W,
        window.innerHeight / DESIGN_H
      );
      document.documentElement.style.setProperty("--stage-scale", String(s));
    };
    fit();
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    return () => {
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
    };
  }, []);

  // Publish the configured colors as CSS variables so any screen can use them
  // (e.g. the plantas navigation fade, global font color, loading/error screens).
  // useLayoutEffect so they're set before the first paint — the loading screen
  // (seeded from the theme cache) shows the configured colors with no flash.
  useLayoutEffect(() => {
    const root = document.documentElement.style;
    root.setProperty("--app-bg-color", bg.color);
    root.setProperty("--app-bg-color-0", hexToRgba(bg.color, 0));
    root.setProperty("--app-font-color", appConfig.fontColor);
    // Secondary/dimmed text: the font color at reduced opacity.
    root.setProperty("--app-font-color-muted", hexToRgba(appConfig.fontColor, 0.78));
    // App-wide UI fonts (admin Config). Load each, then expose as variables that
    // every screen's font-family references. Primary = body; secondary = accent/
    // numbers (the app's original Space Mono). Fall back to the bundled families.
    ensureFontLoaded(appConfig.fontFamily, "app-font-link");
    root.setProperty(
      "--app-font-family",
      appConfig.fontFamily ? `"${appConfig.fontFamily}", "DM Sans", sans-serif` : `"DM Sans", sans-serif`
    );
    ensureFontLoaded(appConfig.fontFamilySecondary, "app-font-link-secondary");
    root.setProperty(
      "--app-font-family-secondary",
      appConfig.fontFamilySecondary
        ? `"${appConfig.fontFamilySecondary}", "Space Mono", monospace`
        : `"Space Mono", monospace`
    );
  }, [bg.color, appConfig.fontColor, appConfig.fontFamily, appConfig.fontFamilySecondary]);

  return (
    <Viewport baseColor={bg.color} gradient={viewportGradient} image={stageBg}>
      {!stageBg && <Pulse gradient={pulseGradient} />}
      <Canvas>{children}</Canvas>
    </Viewport>
  );
}
